import { FormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";

import { ClientModelsDto, ContainerData, EnvironmentDto, isEnvironmentDto } from "../types/types";

interface AntragstellungConfig {
	mode: string;
	modelsUrl: string;
	submitUrl: string;
	attachmentUploadUrl: string;
	attachmentDownloadUrl: string;
	envUrl: string;
	requiresAuthHeader: boolean;
}

let invitationToken: string | null = null;
let config: AntragstellungConfig | null = null;

// Deferred promise — resolves/rejects when loadConfig() completes.
// Any caller of waitForConfig() will wait here until config is ready.
let resolveConfig: ((cfg: AntragstellungConfig) => void) | undefined;
let rejectConfig: ((err: unknown) => void) | undefined;
const configPromise = new Promise<AntragstellungConfig>((resolve, reject) => {
	resolveConfig = resolve;
	rejectConfig = reject;
});

/**
 * Reads the invitation token from the URL hash fragment (if present) and removes it from the URL.
 * Must be called once on application startup before loadConfig().
 */
export function initInvitationToken(): void {
	const hash = window.location.hash;
	if (hash && hash.startsWith("#invitationToken=")) {
		invitationToken = hash.substring("#invitationToken=".length);
		// Clear the token from the URL to prevent leakage via browser history / Referer header
		window.history.replaceState(null, "", window.location.pathname + window.location.search);
	}
}

/**
 * Loads the route configuration from the backend. If an invitation token is present,
 * includes it in the Authorization header so the backend returns invitation-mode routes.
 */
export async function loadConfig(applicationKey: string): Promise<AntragstellungConfig> {
	const headers: HeadersInit = {};
	if (invitationToken) {
		headers["Authorization"] = `Bearer ${invitationToken}`;
	}
	try {
		const response = await fetch(`/extern/antragstellung/config/${applicationKey}`, { headers });
		if (!response.ok) {
			throw new Error(`Config request failed with status ${response.status}`);
		}
		config = (await response.json()) as AntragstellungConfig;
		resolveConfig!(config);
		return config;
	} catch (err) {
		rejectConfig!(err);
		throw err;
	}
}

/**
 * Returns a promise that resolves once loadConfig() has completed successfully.
 * Safe to call before loadConfig() — the promise simply waits until config is ready.
 */
export function waitForConfig(): Promise<AntragstellungConfig> {
	return configPromise;
}

export function getConfig(): AntragstellungConfig {
	if (!config) {
		throw new Error("Config not loaded. Call loadConfig() first.");
	}
	return config;
}

export function getAuthHeaders(): HeadersInit {
	if (invitationToken && config?.requiresAuthHeader) {
		return { Authorization: `Bearer ${invitationToken}` };
	}
	return {};
}

export async function loadModels(applicationKey: string): Promise<ClientModelsDto> {
	const cfg = getConfig();
	const response = await fetch(cfg.modelsUrl, {
		headers: getAuthHeaders()
	});
	if (!response.ok) {
		throw new Error(`Model loading failed with status ${response.status}`);
	}
	const container = (await response.json()) as ContainerData;
	const formModelAsJson = JSON.parse(container.serializedFormModel) as FormModel;

	return {
		formModelAsJson,
		documentModelAsString: container.serializedDocumentModel,
		validationCode: container.validationCode,
		shortName: container.shortName
	};
}

export async function getCurrentEnvironment(): Promise<EnvironmentDto> {
	const cfg = await waitForConfig();
	const response = await fetch(cfg.envUrl);
	const responseObject = await response.json();
	if (isEnvironmentDto(responseObject)) {
		return responseObject;
	}
	throw new Error("Environment-Response is not valid.");
}
